import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Gift, RefreshCw, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSpinWheel } from '../hooks/cabin/useSpinWheel';
import { useConfetti } from '../hooks/cabin/useConfetti';
import { useSoundEffects } from '../hooks/cabin/useSoundEffects';
import CabinErrorState from '../components/cabin/CabinErrorState';

// Wheel segments — weighted per spec
const SEGMENTS = [
  { label_tr: '10 Miles', label_en: '10 Miles', color: '#0EA5E9', textColor: '#fff', weight: 40 },
  { label_tr: '25 Miles', label_en: '25 Miles', color: '#22c55e', textColor: '#fff', weight: 25 },
  { label_tr: '50 Miles', label_en: '50 Miles', color: '#f97316', textColor: '#fff', weight: 15 },
  { label_tr: '100 XP',   label_en: '100 XP',   color: '#a855f7', textColor: '#fff', weight: 10 },
  { label_tr: '250 XP',   label_en: '250 XP',   color: '#ec4899', textColor: '#fff', weight: 5  },
  { label_tr: 'Streak Freeze', label_en: 'Streak Freeze', color: '#eab308', textColor: '#fff', weight: 3 },
  { label_tr: 'Bonus Görev', label_en: 'Bonus Task', color: '#ef4444', textColor: '#fff', weight: 2 },
];

const TOTAL_WEIGHT = SEGMENTS.reduce((s, seg) => s + seg.weight, 0);
const SEGMENT_COUNT = SEGMENTS.length;
const ANGLE_PER = 360 / SEGMENT_COUNT; // equal visual split

// Pick weighted random segment index
function pickWeightedIndex() {
  const r = Math.random() * TOTAL_WEIGHT;
  let acc = 0;
  for (let i = 0; i < SEGMENTS.length; i++) {
    acc += SEGMENTS[i].weight;
    if (r < acc) return i;
  }
  return SEGMENTS.length - 1;
}

// Draw wheel on canvas — lang param selects label_tr or label_en
function drawWheel(canvas, segments, lang) {
  const ctx = canvas.getContext('2d');
  const size = canvas.width;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const arc = (2 * Math.PI) / segments.length;

  ctx.clearRect(0, 0, size, size);

  segments.forEach((seg, i) => {
    const startAngle = i * arc - Math.PI / 2;
    const endAngle = startAngle + arc;

    // Slice
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label — prefer current lang
    const label = lang === 'tr' ? (seg.label_tr || seg.label_en) : (seg.label_en || seg.label_tr);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(startAngle + arc / 2);
    ctx.textAlign = 'right';
    ctx.fillStyle = seg.textColor;
    ctx.font = `bold ${size < 300 ? 10 : 13}px Nunito, sans-serif`;
    ctx.fillText(label, r - 12, 5);
    ctx.restore();
  });

  // Center circle
  ctx.beginPath();
  ctx.arc(cx, cy, 24, 0, 2 * Math.PI);
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 3;
  ctx.stroke();
}

function Countdown({ nextSpinAt }) {
  const { t } = useLanguage();
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    const update = () => {
      if (!nextSpinAt) return;
      const diff = new Date(nextSpinAt) - new Date();
      if (diff <= 0) {
        setRemaining('00:00:00');
        return;
      }
      const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
      const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
      const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
      setRemaining(`${h}:${m}:${s}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [nextSpinAt]);

  return (
    <div className="text-center py-8">
      <div className="text-5xl font-black text-[var(--brand-primary)] mb-2 tabular-nums">
        {remaining}
      </div>
      <p className="text-sm text-[var(--text-muted)]">{t('cabin.spinWheel.comeBack')}</p>
    </div>
  );
}

export default function CabinSpinWheel() {
  const { t, lang } = useLanguage();
  const { status, loading, spinning, error: spinError, fetchToday, spin } = useSpinWheel();
  const { fire } = useConfetti();
  const { play } = useSoundEffects();
  const shouldReduce = useReducedMotion();

  const canvasRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const [finalSegment, setFinalSegment] = useState(null);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  // Ref callback: draw wheel as soon as canvas node is available (robust vs loading-state timing)
  const canvasRefCallback = useCallback(
    (node) => {
      canvasRef.current = node;
      if (node) drawWheel(node, SEGMENTS, lang);
    },
    [lang]
  );

  // Redraw when language changes (label_tr / label_en)
  useEffect(() => {
    if (canvasRef.current) drawWheel(canvasRef.current, SEGMENTS, lang);
  }, [lang]);

  const handleSpin = useCallback(async () => {
    if (spinning || status?.already_used) return;

    const pickedIdx = pickWeightedIndex();
    // Compute final rotation: spin 5+ full turns, stop at picked segment center
    const segAngle = ANGLE_PER * pickedIdx + ANGLE_PER / 2;
    const spins = 5 + Math.floor(Math.random() * 3);
    const targetRotation = rotation + spins * 360 + (360 - segAngle);

    setRotation(targetRotation);
    setFinalSegment(null);

    play('click');

    // API call
    const result = await spin();

    setTimeout(
      () => {
        if (result) {
          setFinalSegment(result);
        } else {
          setFinalSegment(SEGMENTS[pickedIdx]);
        }
        setShowReward(true);
        play('success');
        play('coin');
        if (!shouldReduce) fire({ particleCount: 120 });
      },
      shouldReduce ? 0 : 4200
    );
  }, [spinning, status, rotation, spin, play, shouldReduce, fire]);

  return (
    <div
      className="flex flex-col items-center gap-6 py-8 px-4 max-w-md mx-auto"
      data-testid="spin-wheel-page"
    >
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center mx-auto mb-3 shadow-[0_4px_0_0_#d97706]">
          <Gift size={28} className="text-white" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-black text-[var(--text-main)]">
          {t('cabin.spinWheel.title')}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t('cabin.spinWheel.subtitle')}
        </p>
      </div>

      {loading ? (
        <div className="w-12 h-12 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
      ) : spinError && !status ? (
        <CabinErrorState
          onRetry={fetchToday}
          backPath="/programs/kabin-memurlugu"
          message={spinError}
          testId="spin-wheel-error"
        />
      ) : status?.already_used ? (
        /* Already used today */
        <div className="w-full bg-white border-2 border-[var(--border-light)] rounded-2xl p-6 text-center shadow-[0_4px_0_0_rgba(0,0,0,0.06)]">
          <div className="text-5xl mb-3">🔒</div>
          <h2 className="font-black text-[var(--text-main)] text-lg mb-1">
            {t('cabin.spinWheel.alreadyUsed')}
          </h2>
          <Countdown nextSpinAt={status.next_spin_at} />
          <button
            onClick={fetchToday}
            className="flex items-center gap-2 mx-auto text-sm text-[var(--brand-primary)] font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
          >
            <RefreshCw size={14} aria-hidden="true" />
            {t('common.retry')}
          </button>
        </div>
      ) : (
        /* Wheel available */
        <>
          {/* Pointer */}
          <div className="relative w-full flex justify-center">
            <div
              className="absolute top-0 z-10 w-6 h-8 flex items-end justify-center"
              style={{ left: 'calc(50% - 12px)' }}
              aria-hidden="true"
            >
              <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[20px] border-l-transparent border-r-transparent border-t-rose-500 drop-shadow-md" />
            </div>

            {/* Canvas wheel */}
            <motion.div
              animate={shouldReduce ? {} : { rotate: rotation }}
              transition={
                shouldReduce
                  ? {}
                  : {
                      duration: 4,
                      ease: [0.17, 0.67, 0.12, 0.99],
                    }
              }
              style={{ originX: '50%', originY: '50%' }}
              className="mt-6"
            >
              <canvas
                ref={canvasRefCallback}
                width={280}
                height={280}
                className="rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-4 border-white"
                aria-label={t('cabin.spinWheel.title')}
              />
            </motion.div>
          </div>

          {/* Spin button */}
          <motion.button
            whileHover={spinning || shouldReduce ? {} : { scale: 1.04 }}
            whileTap={spinning || shouldReduce ? {} : { scale: 0.96 }}
            onClick={handleSpin}
            disabled={spinning}
            className="w-full py-4 bg-gradient-to-r from-[var(--brand-primary)] to-indigo-500 text-white font-black text-lg rounded-2xl shadow-[0_6px_0_0_#0369a1] hover:shadow-[0_4px_0_0_#0369a1] hover:translate-y-[2px] transition-all disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid="spin-btn"
          >
            <span className="flex items-center justify-center gap-2">
              {spinning ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('cabin.spinWheel.spinning')}
                </>
              ) : (
                <>
                  <Star size={20} aria-hidden="true" />
                  {t('cabin.spinWheel.spin')}
                </>
              )}
            </span>
          </motion.button>

          {/* Segment legend */}
          <div className="w-full grid grid-cols-2 gap-2">
            {SEGMENTS.map((seg, i) => {
              const label = lang === 'tr' ? seg.label_tr : seg.label_en;
              return (
                <div key={i} className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: seg.color }}
                    aria-hidden="true"
                  />
                  <span>{label}</span>
                  <span className="ml-auto text-gray-400">{seg.weight}%</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Reward popup */}
      <AnimatePresence>
        {showReward && finalSegment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowReward(false)}
            data-testid="reward-popup"
          >
            <motion.div
              initial={shouldReduce ? {} : { scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={shouldReduce ? {} : { scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl"
            >
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-black text-[var(--text-main)] mb-2">
                {t('cabin.spinWheel.congrats')}
              </h2>
              <p className="text-sm text-[var(--text-muted)] mb-5">
                {t('cabin.spinWheel.youWon')}
              </p>
              <div
                className="text-4xl font-black py-4 px-6 rounded-2xl text-white mb-6 shadow-[0_4px_0_0_rgba(0,0,0,0.15)]"
                style={{ backgroundColor: finalSegment.color || '#0EA5E9' }}
              >
                {lang === 'tr'
                  ? finalSegment.label_tr || finalSegment.label || finalSegment.type
                  : finalSegment.label_en || finalSegment.label || finalSegment.type}
              </div>
              <motion.button
                whileHover={shouldReduce ? {} : { scale: 1.02 }}
                whileTap={shouldReduce ? {} : { scale: 0.98 }}
                onClick={() => setShowReward(false)}
                className="w-full py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors"
              >
                {t('cabin.spinWheel.awesome')}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
